<script lang="ts">
  import { onMount } from "svelte";
  import { AudioManager, windLevel } from "../audio/AudioManager";
  import { CLOUDFIELD } from "../config/cloudfield";
  import { CAMERA, FEEDBACK, GAMEPLAY, SCORING } from "../config/gameplay";
  import { colorToCss, PALETTE } from "../config/palette";
  import { GameSession } from "../game/GameSession";
  import type { CrashEvent, GameEvent } from "../game/events";
  import {
    type ReplayFrame,
    replayDuration,
    sampleReplay,
  } from "../game/ReplayRecorder";
  import type { GameSnapshot } from "../game/snapshot";
  import { InputController } from "../input/InputController";
  import { KeyboardController } from "../input/KeyboardController";
  import { LeaderboardValidationError } from "../leaderboard/core";
  import {
    LeaderboardAdapter,
    type LeaderboardViewState,
  } from "../leaderboard/leaderboardAdapter";
  import { createSupabaseLeaderboardGateway } from "../leaderboard/supabaseGateway";
  import { AirportRenderer } from "../rendering/AirportRenderer";
  import { WorldView } from "../rendering/WorldView";
  import { createRunMetadata, toCompletedRun } from "../scoring/completedRun";
  import {
    type OverlayViewport,
    runwayBadges,
    type ScreenBadge,
    type ScreenHint,
    spawnTelegraphs,
    type SpawnTelegraph,
    trafficBadge,
    type TrafficBadge,
    waitingHint,
  } from "./overlays";
  import { type Language, translator } from "./i18n";
  import { addPopup, prunePopups, type ScorePopup } from "./popups";
  import {
    type BestRun,
    createPreferenceStore,
    type RunRecord,
  } from "./preferences";
  import ControlRail from "./components/ControlRail.svelte";
  import ErrorOverlay from "./components/ErrorOverlay.svelte";
  import FpsMeter from "./components/FpsMeter.svelte";
  import GameOverOverlay from "./components/GameOverOverlay.svelte";
  import LeaderboardOverlay from "./components/LeaderboardOverlay.svelte";
  import PauseOverlay from "./components/PauseOverlay.svelte";
  import SettingsOverlay from "./components/SettingsOverlay.svelte";
  import StatsPanel from "./components/StatsPanel.svelte";
  import TutorialOverlay from "./components/TutorialOverlay.svelte";
  import WorldOverlays from "./components/WorldOverlays.svelte";

  /** Clear of the apron, next to the hangars — anchors the parked count. */
  const APRON_ANCHOR = { x: -35, y: -26 };
  const HUD_INTERVAL_MS = 100;

  /**
   * The camera checks this for itself; the DOM half is handled by app.css.
   * This copy is what decides whether a crash is replayed at all — a slowed
   * action replay is exactly the kind of motion the setting is about.
   */
  const reducedMotion =
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false;

  let canvas: HTMLCanvasElement;

  const preferences = createPreferenceStore({
    languages:
      typeof navigator === "undefined" ? undefined : navigator.languages,
  });
  const stored = preferences.preferences;

  const audio = new AudioManager({
    enabled: stored.soundEnabled,
    volume: stored.volume,
  });
  const session = new GameSession({ map: CLOUDFIELD, gameplay: GAMEPLAY });
  const leaderboard = new LeaderboardAdapter(
    createSupabaseLeaderboardGateway(),
  );

  // Built from the session's own seed, not a second reading of the clock:
  // the seed is recorded with the score so the run can be replayed later, and
  // a seed that did not drive this run would name a run nobody can reproduce.
  let runMetadata = createRunMetadata(session.seed);
  let hasDrawnPath = false;

  let snapshot = $state<GameSnapshot>(session.snapshot());
  let leaderboardState = $state<LeaderboardViewState>(leaderboard.state);
  let soundEnabled = $state(stored.soundEnabled);
  let volume = $state(stored.volume);
  let statsVisible = $state(stored.statsVisible);
  let symbolsVisible = $state(stored.symbolsVisible);
  let language = $state<Language>(stored.language);
  let fpsVisible = $state(stored.fpsVisible);
  let tutorialVisible = $state(!stored.tutorialSeen);
  let settingsVisible = $state(false);
  let scoresVisible = $state(false);
  let resumeAfterModal = false;
  let nameError = $state("");
  let best = $state<BestRun | null>(preferences.best);
  let history = $state<readonly RunRecord[]>(preferences.history);
  let isNewBest = $state(false);
  let badges = $state<ScreenBadge[]>([]);
  let traffic = $state<TrafficBadge | null>(null);
  let hint = $state<ScreenHint | null>(null);
  let telegraphs = $state<SpawnTelegraph[]>([]);
  let errorMessage = $state("");
  let fps = $state(0);
  let zoomLevel = $state(1);
  /** Announced to screen readers when the keyboard selection moves. */
  let selection = $state("");
  let popups = $state<ScorePopup[]>([]);
  let flashing = $state(false);
  /** The crash replay holds the game-over dialog back until it has run. */
  let replaying = $state(false);
  /** One white frame on the touch, at the end of the replay. */
  let impactFlashing = $state(false);

  let popupKey = 0;
  let flashTimer: ReturnType<typeof setTimeout> | null = null;
  let impactTimer: ReturnType<typeof setTimeout> | null = null;
  let replayFrames: readonly ReplayFrame[] = [];
  let replayTime = 0;
  let replayEndsAt = 0;
  let crashPosition: { x: number; y: number } | null = null;
  /** The pair that touched: ringed red for as long as the replay runs. */
  let crashIds: ReadonlySet<number> = new Set();
  let crashLevels: ReadonlyMap<number, number> = new Map();

  const t = $derived(translator(language));

  let renderer: AirportRenderer | null = null;
  let world: WorldView | null = null;

  function togglePause(): void {
    // The dialog owns the clock while it is open; `p` must not take it back
    // and leave the run live behind it.
    if (settingsVisible || scoresVisible) {
      return;
    }
    audio.playButtonClick();
    session.togglePause();
    snapshot = session.snapshot();
  }

  /** Raise a floating label where something was earned. */
  function showPopup(
    position: { x: number; y: number },
    text: string,
    color: string,
  ): void {
    if (!renderer || !canvas) {
      return;
    }
    const point = renderer.worldToScreen(position);
    const rect = canvas.getBoundingClientRect();
    if (
      point.x < 0 ||
      point.y < 0 ||
      point.x > rect.width ||
      point.y > rect.height
    ) {
      // Off screen: a label pinned to the edge points at nothing.
      return;
    }
    popupKey += 1;
    popups = addPopup(popups, {
      key: popupKey,
      x: point.x,
      y: point.y,
      text,
      color,
      bornAt: performance.now(),
    });
  }

  function flashStreakBreak(): void {
    flashing = false;
    // Restart the animation rather than let a second break run into the tail
    // of the first: Svelte removes the node and puts a fresh one back.
    if (flashTimer !== null) {
      clearTimeout(flashTimer);
    }
    queueMicrotask(() => {
      flashing = true;
    });
    flashTimer = setTimeout(() => {
      flashing = false;
      flashTimer = null;
    }, FEEDBACK.streakFlashMs);
  }

  /**
   * Play the last few seconds back, slowed down, before the dialog lands.
   *
   * The recording is poses only, so this is the renderer being fed a
   * different source for a few seconds — the session is already over and is
   * neither read nor advanced while it runs.
   */
  function startCrashReplay(event: CrashEvent): void {
    renderer?.focusCrash(event.position, event.immediate ?? false);
    crashPosition = { x: event.position.x, y: event.position.y };
    crashIds = new Set(event.aircraftIds ?? []);
    // Full severity: the two that touched get the tightest, reddest ring the
    // proximity display can draw, which is exactly what happened to them.
    crashLevels = new Map([...crashIds].map((id) => [id, 1]));

    const frames = session.replayFrames();
    if (event.immediate || reducedMotion || replayDuration(frames) <= 0) {
      world?.showCrash(event.position);
      return;
    }

    replayFrames = frames;
    replayTime = frames[0]!.time;
    replayEndsAt = frames[frames.length - 1]!.time;
    replaying = true;
  }

  /** Ends the replay early or on time; the fireball marks the spot after. */
  function endCrashReplay(): void {
    if (!replaying) {
      return;
    }
    replaying = false;
    replayFrames = [];
    if (crashPosition) {
      world?.showCrash(crashPosition);
    }
    flashImpact();
  }

  /** The white frame the fireball lands on. */
  function flashImpact(): void {
    if (reducedMotion) {
      return;
    }
    if (impactTimer !== null) {
      clearTimeout(impactTimer);
    }
    impactFlashing = true;
    impactTimer = setTimeout(() => {
      impactFlashing = false;
      impactTimer = null;
    }, FEEDBACK.impactFlashMs);
  }

  function restart(): void {
    audio.playButtonClick();
    replaying = false;
    replayFrames = [];
    crashPosition = null;
    crashIds = new Set();
    crashLevels = new Map();
    impactFlashing = false;
    popups = [];
    // A fresh seed per run: without one the next run continues this run's
    // generator, and the seed recorded against its score would not reproduce
    // it.
    session.reset(renderer?.viewBounds ?? session.bounds, Date.now());
    world?.clearCrash();
    renderer?.resetFocus();
    runMetadata = createRunMetadata(session.seed);
    // Without the reset this carries the previous run's rank into the next
    // game-over dialog — permanently, if that run scores nothing and is
    // therefore never submitted.
    leaderboard.reset();
    leaderboardState = leaderboard.state;
    hasDrawnPath = false;
    isNewBest = false;
    snapshot = session.snapshot();
  }

  function toggleSound(): void {
    soundEnabled = !soundEnabled;
    audio.setEnabled(soundEnabled);
    preferences.update({ soundEnabled });
    audio.playButtonClick();
  }

  function setVolume(next: number): void {
    volume = next;
    audio.setVolume(next);
    preferences.update({ volume: next });
  }

  async function saveName(name: string): Promise<void> {
    // A refused name is a normal outcome now that some are reserved, and an
    // unhandled rejection here would leave the form looking like it hung.
    nameError = "";
    try {
      leaderboardState = await leaderboard.updatePlayerName(name);
    } catch (error) {
      nameError =
        error instanceof LeaderboardValidationError
          ? t("gameover.nameRefused")
          : t("gameover.unavailable");
    }
  }

  function toggleStats(): void {
    statsVisible = !statsVisible;
    preferences.update({ statsVisible });
  }

  function toggleSymbols(): void {
    symbolsVisible = !symbolsVisible;
    world?.setSymbolsVisible(symbolsVisible);
    preferences.update({ symbolsVisible });
    audio.playButtonClick();
  }

  function toggleFps(): void {
    fpsVisible = !fpsVisible;
    preferences.update({ fpsVisible });
    audio.playButtonClick();
  }

  function selectLanguage(next: Language): void {
    if (next === language) {
      return;
    }
    language = next;
    preferences.update({ language });
    audio.playButtonClick();
  }

  /**
   * A dialog opened from the rail pauses the run.
   *
   * These cover the airport and take a while to read; leaving the sky live
   * behind one would end runs while the player is picking a language or
   * reading the board. `resumeAfterModal` is what stops closing it from
   * un-pausing a run the player had paused themselves.
   */
  function pauseForModal(): void {
    resumeAfterModal = snapshot.phase === "playing";
    if (resumeAfterModal) {
      session.togglePause();
      snapshot = session.snapshot();
    }
    audio.playButtonClick();
  }

  function releaseModal(): void {
    if (resumeAfterModal && snapshot.phase === "paused") {
      session.togglePause();
      snapshot = session.snapshot();
    }
    resumeAfterModal = false;
    audio.playButtonClick();
  }

  function openSettings(): void {
    pauseForModal();
    settingsVisible = true;
  }

  function closeSettings(): void {
    settingsVisible = false;
    releaseModal();
  }

  function openScores(): void {
    pauseForModal();
    scoresVisible = true;
    reloadBoard();
  }

  function closeScores(): void {
    scoresVisible = false;
    releaseModal();
  }

  /** The board is never live: it is read when it is opened, and on request. */
  function reloadBoard(): void {
    void leaderboard.loadBoard().then((state) => {
      leaderboardState = state;
    });
  }

  function dismissTutorial(): void {
    tutorialVisible = false;
    preferences.update({ tutorialSeen: true });
    audio.unlock();
    audio.playButtonClick();
  }

  function rotate(direction: "left" | "right"): void {
    audio.playButtonClick();
    renderer?.rotateBy(
      direction === "left"
        ? CAMERA.rotationStepDegrees
        : -CAMERA.rotationStepDegrees,
    );
  }

  /** Wheel and pinch pass a raw factor; the buttons and keys pass a step. */
  function zoomBy(factor: number): void {
    renderer?.zoomBy(factor);
    zoomLevel = renderer?.zoomLevel() ?? zoomLevel;
  }

  function zoomStep(direction: "in" | "out"): void {
    audio.playButtonClick();
    zoomBy(direction === "in" ? CAMERA.zoomStep : 1 / CAMERA.zoomStep);
  }

  function handleEvent(event: GameEvent): void {
    switch (event.type) {
      case "land": {
        audio.playCabinChime();
        // A ring of dust at the wheels, and rubber down the roll direction.
        world?.spawnDust(event.position);
        world?.spawnSkid(event.position, event.heading);
        const streak = session.snapshot().score.streak;
        showPopup(
          event.position,
          streak >= FEEDBACK.streakLabelMinimum
            ? `+${event.points} ×${streak}`
            : `+${event.points}`,
          colorToCss(PALETTE.runway[event.color].marker),
        );
        break;
      }
      case "takeoff":
        audio.playTakeoff();
        // Thrown backwards down the runway: thrust, not impact.
        world?.spawnDust(event.position, event.heading);
        showPopup(
          event.position,
          `+${event.points}`,
          colorToCss(PALETTE.runway[event.color].marker),
        );
        break;
      case "streak-break":
        if (event.streak >= FEEDBACK.streakFlashMinimum) {
          flashStreakBreak();
        }
        break;
      case "reject":
        audio.playReject();
        break;
      case "spawn":
        audio.playSpawn();
        break;
      case "near-miss":
        audio.playNearMiss();
        // The streak flash says something went wrong; this says what it cost.
        showPopup(
          event.position,
          `−${SCORING.nearMissPenalty}`,
          colorToCss(PALETTE.warning),
        );
        break;
      case "crash":
        audio.playCollisionAlert();
        startCrashReplay(event);
        {
          // The board only accepts runs with at least one landing; submitting
          // a scoreless run would come back as "unavailable" and read like an
          // outage.
          const finalScore = session.snapshot().score;
          const submittedRun = runMetadata;
          isNewBest = preferences.recordRun({
            landings: finalScore.landings,
            departures: finalScore.departures,
            durationSeconds: finalScore.durationSeconds,
          });
          best = preferences.best;
          history = preferences.history;
          const publish = (state: LeaderboardViewState): void => {
            // Drop a resolution that lost the race to a newer run.
            if (runMetadata === submittedRun) {
              leaderboardState = state;
            }
          };
          if (finalScore.landings > 0) {
            // Strictly after the insert, never alongside it. A parallel read
            // cannot contain this run, and if it lands last it overwrites the
            // fresh list — the player is told "saved, rank #1" over a board
            // that says it is empty.
            void leaderboard
              .submitCompletedRun(toCompletedRun(finalScore, submittedRun))
              .then((state) => {
                publish(state);
                return leaderboard.loadBoard().then(publish);
              });
            // Show "Saving your run…" instead of an empty dialog while the
            // request is in flight; the adapter sets this synchronously.
            leaderboardState = leaderboard.state;
          } else {
            // Worth showing even for a run too small to submit.
            void leaderboard.loadBoard().then(publish);
          }
        }
        break;
      default:
        break;
    }
  }

  function updateOverlays(view: AirportRenderer, current: GameSnapshot): void {
    const rect = canvas.getBoundingClientRect();
    const viewport: OverlayViewport = {
      width: rect.width,
      height: rect.height,
      project: (point) => view.worldToScreen(point),
    };

    badges = runwayBadges(
      current.runwayStatuses,
      CLOUDFIELD.runways,
      viewport,
      symbolsVisible,
    );
    traffic = trafficBadge(APRON_ANCHOR, current.traffic.occupied, viewport);
    // Inbound traffic is only telegraphed while the run is live; on a pause or
    // a game over the countdown would keep ticking on a frozen board.
    telegraphs =
      current.phase === "playing"
        ? spawnTelegraphs(session.spawnWarnings, viewport, symbolsVisible)
        : [];

    const waiting =
      hasDrawnPath || current.phase !== "playing"
        ? null
        : (session.aircraft.find(
            (aircraft) =>
              aircraft.state === "flying" && aircraft.path.length === 0,
          )?.pos ?? null);
    hint = waitingHint(waiting, t("hint.drag"), viewport);
  }

  onMount(() => {
    let view: AirportRenderer;
    try {
      view = new AirportRenderer(canvas);
    } catch (error) {
      errorMessage = t("error.webgl");
      console.error(error);
      return;
    }

    renderer = view;
    const scene = new WorldView(view);
    world = scene;
    scene.setSymbolsVisible(symbolsVisible);
    scene.build(CLOUDFIELD, performance.now() / 1000);
    void scene.loadAssets().catch((error: unknown) => {
      console.warn("Aircraft model failed to load", error);
    });

    const resize = (): void => {
      const rect = canvas.getBoundingClientRect();
      session.setBounds(view.resize(rect.width, rect.height));
    };
    resize();
    window.addEventListener("resize", resize);
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);

    // Safari only lets an AudioContext start inside a user gesture, so the
    // first tap or key press opens it — otherwise the first cue is silent.
    const unlockAudio = (): void => audio.unlock();
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });

    // Three stops drawing on its own when the GPU drops the context, but the
    // simulation would keep running behind a frozen picture — and could end
    // the run on a collision the player never saw coming.
    view.onContextLost = () => {
      session.pause();
      snapshot = session.snapshot();
    };

    // A hidden tab stops rAF without pausing, so the player comes back to a
    // still-live board mid-air. Pause on the way out; never auto-resume — the
    // PauseOverlay hands the clock back when they are ready.
    const handleHidden = (): void => {
      if (document.hidden) {
        session.pause();
        snapshot = session.snapshot();
      }
    };
    document.addEventListener("visibilitychange", handleHidden);

    // The replay is a flourish, not a wait: any input cuts to the dialog.
    const skipReplay = (): void => endCrashReplay();
    window.addEventListener("pointerdown", skipReplay);
    window.addEventListener("keydown", skipReplay);

    // Escape closes a rail dialog. The keyboard controller also sees it and
    // drops its selection, which is the right thing to happen anyway.
    const closeSettingsOnEscape = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") {
        return;
      }
      if (settingsVisible) {
        closeSettings();
      } else if (scoresVisible) {
        closeScores();
      }
    };
    window.addEventListener("keydown", closeSettingsOnEscape);

    const input = new InputController({
      canvas,
      session,
      toWorld: (x, y) => view.screenToWorld(x, y),
      worldUnitsForPixels: (pixels) => view.worldUnitsForPixels(pixels),
      onGrab: () => {
        hasDrawnPath = true;
      },
      onZoom: zoomBy,
    });

    const keyboard = new KeyboardController({
      target: window,
      session,
      onSelect: (aircraft) => {
        hasDrawnPath = true;
        // With shapes on, the announcement carries both channels too — the
        // colour name alone is what leaves a colour-blind player guessing.
        selection = aircraft
          ? t("a11y.selected", {
              colour: symbolsVisible
                ? `${t(`colour.${aircraft.kind}`)} (${t(`symbol.${aircraft.kind}`)})`
                : t(`colour.${aircraft.kind}`),
            })
          : t("a11y.noneSelected");
      },
      onTogglePause: togglePause,
      onRotate: rotate,
      onZoom: zoomStep,
      onToggleSound: toggleSound,
    });

    audio.setEnabled(soundEnabled);

    // Read-only handle on the running game. The bundle ships unminified so it
    // can be debugged where it is deployed; this is the same bargain, and it
    // is what lets the browser suite drive a real approach.
    window.__pixelflight = {
      session,
      runways: CLOUDFIELD.runways,
      worldToScreen: (point) => view.worldToScreen(point),
    };

    let previous = performance.now();
    let hudAt = 0;
    let frame = 0;
    // Averaged over a window rather than taken from one delta: a single frame
    // reads as noise, and a readout that flickers red is worse than none.
    let fpsAt = previous;
    let fpsFrames = 0;

    const tick = (now: number): void => {
      frame = requestAnimationFrame(tick);
      // Clamped the same way the simulation clamps its own delta, so a hidden
      // tab cannot fast forward a replay the way it cannot fast forward a run.
      const delta = Math.min(now - previous, GAMEPLAY.maxFrameSeconds * 1000);
      session.update(now - previous);
      previous = now;

      for (const event of session.drainEvents()) {
        handleEvent(event);
      }

      const time = now / 1000;
      const paused = session.phase !== "playing";
      if (replaying) {
        // The closer the impact gets, the slower the footage runs.
        const toImpact = Math.max(0, replayEndsAt - replayTime);
        const tail =
          FEEDBACK.replayTailSeconds > 0
            ? Math.min(1, toImpact / FEEDBACK.replayTailSeconds)
            : 1;
        const rate =
          FEEDBACK.replayRate *
          (FEEDBACK.replayTailRate + (1 - FEEDBACK.replayTailRate) * tail);
        replayTime += (delta / 1000) * rate;
        const finished = replayTime >= replayEndsAt;
        const cast = sampleReplay(
          replayFrames,
          Math.min(replayTime, replayEndsAt),
        );
        // The replay is its own source of truth for the frame: routes and the
        // hover highlight belong to a run that has already ended.
        scene.syncAircraft(cast, null, crashIds, time, false, crashLevels);
        scene.syncPaths(cast, null, null, null, time);
        if (finished) {
          endCrashReplay();
        }
      } else {
        scene.syncAircraft(
          session.aircraft,
          session.hoveredId,
          session.warningIds,
          time,
          paused,
          session.warningLevels,
        );
        scene.syncPaths(
          session.aircraft,
          session.drawing,
          session.hoveredId,
          session.getLandingIntent(),
          time,
        );
      }
      view.render();

      fpsFrames += 1;
      const fpsElapsed = now - fpsAt;
      if (fpsElapsed >= FEEDBACK.fpsSampleMs) {
        // A backgrounded tab stops being called at all, so the window that
        // spans the gap is thrown away instead of reported as a stall.
        if (fpsElapsed < FEEDBACK.fpsSampleMs * 4) {
          fps = (fpsFrames * 1000) / fpsElapsed;
        }
        fpsFrames = 0;
        fpsAt = now;
      }

      if (now - hudAt >= HUD_INTERVAL_MS || session.phase !== snapshot.phase) {
        hudAt = now;
        const current = session.snapshot();
        snapshot = current;
        updateOverlays(view, current);
        popups = prunePopups(popups, now, FEEDBACK.popupMs);
        // The view eases out as the sky fills, so the difficulty ramp is
        // something the player feels before they can count it.
        view.setTrafficLoad(current.airborneCount / CAMERA.loadZoomTraffic);
        // Only once the context is open: asking for it before the unlocking
        // gesture builds a suspended context the browser then complains about.
        if (audio.isRunning) {
          audio.setAmbient(
            current.phase === "playing"
              ? windLevel(current.airborneCount, {
                  quiet: FEEDBACK.windQuietLevel,
                  busy: FEEDBACK.windBusyLevel,
                  busyAt: FEEDBACK.windBusyTraffic,
                })
              : 0,
          );
        }
      }
    };
    frame = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frame);
      input.dispose();
      keyboard.dispose();
      observer.disconnect();
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", handleHidden);
      view.onContextLost = null;
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
      window.removeEventListener("pointerdown", skipReplay);
      window.removeEventListener("keydown", skipReplay);
      window.removeEventListener("keydown", closeSettingsOnEscape);
      if (flashTimer !== null) {
        clearTimeout(flashTimer);
        flashTimer = null;
      }
      if (impactTimer !== null) {
        clearTimeout(impactTimer);
        impactTimer = null;
      }
      audio.dispose();
      delete window.__pixelflight;
      scene.dispose();
      view.dispose();
      renderer = null;
      world = null;
    };
  });
</script>

<svelte:head>
  <title>PixelFlightControl</title>
  <meta
    name="description"
    content="The sky (and your endurance) is the limit!"
  />
</svelte:head>

<main data-app="airport">
  <canvas
    bind:this={canvas}
    class:is-shaken={flashing}
    aria-label="Airport simulation"
  ></canvas>

  {#if flashing}
    <div class="flash" aria-hidden="true"></div>
  {/if}

  {#if impactFlashing}
    <div class="flash flash--impact" aria-hidden="true"></div>
  {/if}

  <WorldOverlays
    {badges}
    {traffic}
    {hint}
    {popups}
    {telegraphs}
    popupLifeMs={FEEDBACK.popupMs}
  />

  <p class="visually-hidden" aria-live="polite">{selection}</p>

  <div class="hud">
    <ControlRail
      paused={snapshot.phase === "paused"}
      canZoomIn={zoomLevel < CAMERA.zoomMax - 1e-3}
      canZoomOut={zoomLevel > CAMERA.zoomMin + 1e-3}
      {t}
      onTogglePause={togglePause}
      onRotate={rotate}
      onZoom={zoomStep}
      onOpenScores={openScores}
      onOpenSettings={openSettings}
    />
    {#if statsVisible}
      <StatsPanel score={snapshot.score} {t} />
    {/if}
    {#if fpsVisible}
      <FpsMeter {fps} criticalAt={FEEDBACK.fpsCriticalFps} {t} />
    {/if}
  </div>

  {#if tutorialVisible}
    <TutorialOverlay {t} onDismiss={dismissTutorial} />
  {/if}

  {#if settingsVisible}
    <SettingsOverlay
      {soundEnabled}
      {volume}
      {statsVisible}
      {fpsVisible}
      {symbolsVisible}
      {language}
      {t}
      onToggleSound={toggleSound}
      onVolume={setVolume}
      onToggleStats={toggleStats}
      onToggleFps={toggleFps}
      onToggleSymbols={toggleSymbols}
      onSelectLanguage={selectLanguage}
      onClose={closeSettings}
    />
  {/if}

  {#if scoresVisible}
    <LeaderboardOverlay
      leaderboard={leaderboardState}
      {history}
      {t}
      onClose={closeScores}
      onReload={reloadBoard}
    />
  {/if}

  <!-- The pause dialog would otherwise stack behind a rail dialog, which
       pauses the run itself. -->
  {#if snapshot.phase === "paused" && !tutorialVisible && !settingsVisible && !scoresVisible}
    <PauseOverlay {t} onResume={togglePause} onRestart={restart} />
  {/if}

  {#if snapshot.phase === "gameover" && !replaying}
    <GameOverOverlay
      score={snapshot.score}
      leaderboard={leaderboardState}
      {best}
      {isNewBest}
      {t}
      onRestart={restart}
      {nameError}
      onSaveName={(name) => void saveName(name)}
    />
  {/if}

  {#if errorMessage}
    <ErrorOverlay
      message={errorMessage}
      retryLabel={t("error.retry")}
      onRetry={() => (errorMessage = "")}
    />
  {/if}
</main>
